<script>
  import { onMount } from 'svelte';
  import { router } from '@inertiajs/svelte';
  import ApplicationLayout from '@/components/ApplicationLayout/index.svelte';
  import Toast from '@/components/Toast.svelte';
  import Button from '@/components/Button.svelte';
  
  let { property, pending_images = [], properties = [], user = {} } = $props();

  // Quick capture now works on a selection: tap images to select, then assign
  // the whole set as a single item or container. Selecting nothing falls back
  // to acting on every pending image is avoided — the user must pick at least
  // one. The first selected image becomes the new record's cover.
  let selectedIds = $state([]);
  let selectedPropertyId = $state(property?.id || '');
  let showCamera = $state(false);
  let videoStream = null;
  let videoElement = $state(null);
  
  let isUploading = $state(false);
  let uploadProgress = $state(0);
  
  let toast = $state({
    show: false,
    message: '',
    type: 'info'
  });
  
  onMount(() => {
    if (!selectedPropertyId && properties.length > 0) {
      selectedPropertyId = properties[0].id;
    }
  });
  
  function showToast(message, type = 'info') {
    toast = {
      show: true,
      message,
      type
    };
    
    // Reset toast state after 3 seconds
    setTimeout(() => {
      toast = {
        show: false,
        message: '',
        type: 'info'
      };
    }, 3000);
  }
  
  function toggleSelection(id) {
    if (selectedIds.includes(id)) {
      selectedIds = selectedIds.filter((selected) => selected !== id);
    } else {
      selectedIds = [...selectedIds, id];
    }
  }

  function clearSelection() {
    selectedIds = [];
  }

  function reloadPendingImages() {
    const reloadUrl = property
      ? `/properties/${property.id}/pending_images`
      : '/pending_images';
    router.visit(reloadUrl, { only: ['pending_images'], preserveScroll: true });
  }

  // Assign the selected images together into one new item or container. `kind`
  // is 'item' or 'container'. Uses the collection route with a pending_image_ids
  // array; the first selected image becomes the cover.
  async function assignSelected(kind) {
    if (!selectedPropertyId) {
      showToast('Please select a property first', 'warning');
      return;
    }
    if (selectedIds.length === 0) {
      showToast('Select at least one image first', 'warning');
      return;
    }

    const action = kind === 'container' ? 'assign_to_container' : 'assign_to_item';
    const url = property
      ? `/properties/${property.id}/pending_images/${action}`
      : `/pending_images/${action}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          property_id: selectedPropertyId,
          pending_image_ids: selectedIds
        })
      });

      if (response.ok) {
        clearSelection();
        reloadPendingImages();
      } else {
        const data = await response.json();
        showToast(data.errors || `Failed to assign images to ${kind}`, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast(`Failed to assign images to ${kind}`, 'error');
    }
  }

  // Delete every selected pending image.
  async function deleteSelected() {
    if (selectedIds.length === 0) {
      showToast('Select at least one image first', 'warning');
      return;
    }

    try {
      await Promise.all(
        selectedIds.map((id) => {
          const url = property
            ? `/properties/${property.id}/pending_images/${id}`
            : `/pending_images/${id}`;
          return fetch(url, {
            method: 'DELETE',
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            }
          });
        })
      );
      clearSelection();
      reloadPendingImages();
    } catch (error) {
      console.error('Error:', error);
      showToast('Failed to delete images', 'error');
    }
  }
  
  async function startCamera() {
    if (!selectedPropertyId) {
      alert('Please select a property first');
      return;
    }
    
    try {
      videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoElement.srcObject = videoStream;
      showCamera = true;
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Failed to access camera');
    }
  }
  
  function stopCamera() {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      videoStream = null;
    }
    showCamera = false;
  }
  
  async function capturePhoto() {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    canvas.getContext('2d').drawImage(videoElement, 0, 0);
    
    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('image', blob, 'photo.jpg');
      
      const url = property 
        ? `/properties/${property.id}/pending_images`
        : '/pending_images';
        
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: formData
        });
        
        if (response.ok) {
          stopCamera();
          router.reload();
        } else {
          const data = await response.json();
          alert(data.errors || 'Failed to upload photo');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Failed to upload photo');
      }
    }, 'image/jpeg');
  }
  
  async function handleFileUpload(event) {
    if (!selectedPropertyId) {
      showToast('Please select a property first', 'warning');
      event.target.value = ''; // Clear the input
      return;
    }
    
    const files = event.target.files;
    if (!files.length) return;
    
    if (files.length > 15) {
      showToast('You can only upload up to 15 images at once', 'error');
      event.target.value = ''; // Clear the input
      return;
    }
    
    isUploading = true;
    uploadProgress = 0;
    
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('images[]', file);
    });
    
    const url = property 
      ? `/properties/${property.id}/pending_images`
      : '/pending_images';
      
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.errors) {
        showToast(data.errors, 'error');
        event.target.value = ''; // Clear the input on error
      } else {
        router.reload({ preserveScroll: true });
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('Failed to upload images', 'error');
      event.target.value = ''; // Clear the input on error
    } finally {
      isUploading = false;
      uploadProgress = 0;
    }
  }
</script>

<svelte:head>
  <title>Pending Images {property ? `- ${property.name}` : ''} | Maset</title>
</svelte:head>

<ApplicationLayout {user}>
  <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
    <div class="px-4 py-6 sm:px-0">
    <!-- Property Header -->
    <div class="relative rounded-2xl overflow-hidden mb-8">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-900 via-brand-700 to-teal-600"></div>
      <div class="relative px-6 py-8 sm:px-8 sm:py-12">
        <div class="flex justify-between items-start">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Quick Capture</h1>
            <p class="text-brand-100 text-lg">
              Capture and organize items for {property.name}
            </p>
            <p class="text-brand-50 text-sm mt-1">
              Take multiple photos and quickly sort them into items or containers
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <a href={`/properties/${property.id}`} class="text-white hover:text-white/80 font-medium inline-flex items-center">
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Property
            </a>
          </div>
        </div>
      </div>
    </div>

      <div class="pending-images-container">
        {#if isUploading}
          <div class="upload-overlay">
            <div class="upload-content">
              <div class="spinner"></div>
              <p class="mt-4 text-lg font-medium text-gray-900">Uploading images...</p>
              <p class="mt-2 text-sm text-gray-500">Please wait while we process your images</p>
            </div>
          </div>
        {/if}
        
        {#if !property}
          <div class="property-selector mb-8">
            <label for="property" class="block text-sm font-medium text-gray-700">Select Property:</label>
            <select 
              id="property" 
              bind:value={selectedPropertyId}
              class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand-600 focus:border-brand-600 sm:text-sm rounded-md"
            >
              <option value="">Choose a property...</option>
              {#each properties as property}
                <option value={property.id}>{property.name}</option>
              {/each}
            </select>
          </div>
        {/if}
        
        <div class="upload-section">
          <div class="flex space-x-4 justify-center">
            <Button variant="outline" onclick={startCamera}>
              <svg class="h-5 w-5 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="hidden sm:inline">Take Photo</span>
            </Button>
            
            <div class="relative">
              <input
                type="file"
                accept="image/*"
                multiple
                onchange={handleFileUpload}
                class="hidden"
                id="file-upload"
              />
              <Button variant="outline" onclick={() => document.getElementById('file-upload').click()}>
                <svg class="h-5 w-5 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span class="hidden sm:inline">Upload Images (max 15)</span>
              </Button>
            </div>
          </div>
        </div>
        
        {#if showCamera}
          <div class="camera-container">
            <!-- Live camera preview: muted, no audio track to caption -->
            <video
              bind:this={videoElement}
              autoplay
              playsinline
              muted
              class="w-full max-w-lg mx-auto rounded-lg shadow-lg"
            ></video>
            <div class="flex justify-center space-x-4 mt-4">
              <Button variant="outline" onclick={capturePhoto}>
                Capture
              </Button>
              <Button variant="outline" onclick={stopCamera}>
                Cancel
              </Button>
            </div>
          </div>
        {/if}
        
        <div class="image-grid-section">
          {#if pending_images.length > 0}
            <p class="selection-hint">
              Tap images to select, then mark the selection as an item or
              container. The first one you select becomes the thumbnail.
              {#if selectedIds.length > 0}
                <strong>{selectedIds.length} selected</strong>
                <button type="button" class="clear-link" onclick={clearSelection}>Clear</button>
              {/if}
            </p>
            <div class="image-grid">
              {#each pending_images as image (image.id)}
                {@const order = selectedIds.indexOf(image.id)}
                <button
                  type="button"
                  class="grid-image {order > -1 ? 'selected' : ''}"
                  onclick={() => toggleSelection(image.id)}
                  aria-pressed={order > -1}
                >
                  <img src={image.image_url} alt="Pending upload awaiting review" />
                  {#if order > -1}
                    <span class="selection-badge">{order + 1}</span>
                  {/if}
                </button>
              {/each}
            </div>
          {:else}
            <div class="no-images">
              No pending images. Take a photo or upload images to get started.
            </div>
          {/if}
        </div>

        {#if pending_images.length > 0}
          <div class="action-buttons">
            <Button variant="outline" onclick={() => assignSelected('item')}>
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Item
            </Button>
            <Button variant="outline" onclick={() => assignSelected('container')}>
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Container
            </Button>
            <Button variant="outline" onclick={deleteSelected}>
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </Button>
          </div>
        {/if}
      </div>
    </div>
  </div>

  {#if toast.show}
    <Toast message={toast.message} type={toast.type} />
  {/if}
</ApplicationLayout>

<style>
  .pending-images-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .property-selector {
    margin-bottom: 12px;
  }
  
  .upload-section {
    margin-bottom: 32px;
  }
  
  .camera-container {
    margin-bottom: 32px;
  }
  
  .image-grid-section {
    margin-bottom: 32px;
  }

  .selection-hint {
    text-align: center;
    color: #555;
    font-size: 0.9rem;
    margin-bottom: 16px;
  }

  .clear-link {
    background: none;
    border: none;
    color: #0d9488;
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.9rem;
    padding: 0 4px;
  }

  .image-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (min-width: 640px) {
    .image-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (min-width: 1024px) {
    .image-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .grid-image {
    position: relative;
    aspect-ratio: 1 / 1;
    padding: 0;
    border: 3px solid transparent;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    background: white;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
    transition: border-color 0.15s ease, transform 0.1s ease;
  }

  .grid-image:hover {
    transform: translateY(-2px);
  }

  .grid-image.selected {
    border-color: #0d9488;
  }

  .grid-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .selection-badge {
    position: absolute;
    top: 6px;
    left: 6px;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    background: #0d9488;
    color: white;
    font-size: 0.8rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    padding: 0 20px;
  }
  
  .progress {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: rgba(0,0,0,0.5);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    z-index: 10;
  }
  
  .no-images {
    text-align: center;
    color: #666;
    font-size: 1.2em;
  }
  
  .upload-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
  }
  
  .upload-content {
    text-align: center;
    padding: 2rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
</style> 